import type { PermissionEvaluator } from "../../permissions";
import type { StoredAuthUser } from "../../stores/authStore";
import type { AppFeatureDefinition, FeatureNavNode } from "../contracts";
import { mergeFeatureNavNodes } from "./mergeFeatureNavNodes";

function byOrderThenId(a: FeatureNavNode, b: FeatureNavNode): number {
  const orderDelta =
    (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);

  return orderDelta !== 0 ? orderDelta : a.id.localeCompare(b.id);
}

/**
 * Drops nodes the user cannot reach.
 *
 * A group (a node with children and no `path`) survives only if at least one
 * descendant survives — otherwise the drawer shows headings that open nothing,
 * which is exactly how the legacy navigation misbehaved.
 */
type UserType = StoredAuthUser["userType"];

function filterNode(
  node: FeatureNavNode,
  permissions: PermissionEvaluator,
  userType: UserType | undefined,
): FeatureNavNode | undefined {
  // A client-only hide, independent of permissions -- see FeatureNavNode.hidden.
  if (node.hidden === true) {
    return undefined;
  }

  // Offered to some user types only -- see FeatureNavNode.userTypes.
  if (
    node.userTypes !== undefined &&
    (userType === undefined || !node.userTypes.includes(userType))
  ) {
    return undefined;
  }

  // An explicit gate on the node prunes the whole subtree, whether it is a
  // leaf or a group. Groups normally declare none and inherit visibility from
  // their children.
  if (!permissions.satisfies(node.requiredPermissions)) {
    return undefined;
  }

  if (node.children !== undefined && node.children.length > 0) {
    const children = node.children
      .map((child) => filterNode(child, permissions, userType))
      .filter((child): child is FeatureNavNode => child !== undefined)
      .sort(byOrderThenId);

    if (children.length === 0) {
      return undefined;
    }

    return { ...node, children };
  }

  // A leaf that goes nowhere is a configuration mistake, not a heading.
  return node.path === undefined ? undefined : node;
}

/**
 * `mergeFeatureNavNodes` combines same-id nodes (e.g. every CORE feature's
 * shared "core" rail item) by concatenating their children, but has no
 * opinion on order — it just preserves whichever sequence the contributing
 * features were iterated in. `filterNode` above already sorts a node's own,
 * single-feature-owned children before merging, but that sort doesn't survive
 * the merge once two features' arrays are spliced together. So the merged
 * tree needs its own pass, at every level, or a node's `order` only holds up
 * until the first id it shares with another feature.
 */
function sortTree(
  nodes: readonly FeatureNavNode[],
): readonly FeatureNavNode[] {
  return [...nodes].sort(byOrderThenId).map((node) =>
    node.children !== undefined
      ? { ...node, children: sortTree(node.children) }
      : node,
  );
}

export function resolveFeatureNavigation(
  modules: readonly AppFeatureDefinition[],
  permissions: PermissionEvaluator,
  /** The signed-in user's type, for nodes offered to some types only. */
  userType?: UserType,
): readonly FeatureNavNode[] {
  // The merge happens after filtering, never before: merging first would let
  // one feature's requiredPermissions gate a sibling's drawer items that share
  // its rail node id (see mergeFeatureNavNodes).
  const filtered = modules
    .filter((module) => permissions.satisfies(module.requiredPermissions))
    .flatMap((module) => module.navigation ?? [])
    .map((node) => filterNode(node, permissions, userType))
    .filter((node): node is FeatureNavNode => node !== undefined);

  return sortTree(mergeFeatureNavNodes(filtered));
}

/** Modules whose gate the current user passes. */
export function resolveVisibleFeatures(
  modules: readonly AppFeatureDefinition[],
  permissions: PermissionEvaluator,
): readonly AppFeatureDefinition[] {
  return modules.filter((module) =>
    permissions.satisfies(module.requiredPermissions),
  );
}

/**
 * Longest-prefix match of the current location against the navigation tree,
 * so `/companies/admins/42/edit` resolves to the admins node rather than the
 * shorter `/companies` rail item.
 */
export function findActiveNavNode(
  nodes: readonly FeatureNavNode[],
  pathname: string,
): FeatureNavNode | undefined {
  const normalised = pathname.replace(/\/+$/, "");

  let best: FeatureNavNode | undefined;
  let bestLength = -1;
  let bestDepth = -1;

  function visit(node: FeatureNavNode, depth: number): void {
    const candidates = [
      ...(node.path === undefined ? [] : [node.path]),
      ...(node.activePaths ?? []),
    ];

    for (const candidate of candidates) {
      const target = candidate.replace(/\/+$/, "");
      const isMatch =
        normalised === target || normalised.startsWith(`${target}/`);
      // A rail node usually repeats its first child's path; on that tie the
      // child is the more specific answer, and the shell maps it back to the
      // rail ancestor anyway.
      const isBetter =
        target.length > bestLength ||
        (target.length === bestLength && depth > bestDepth);

      if (isMatch && isBetter) {
        best = node;
        bestLength = target.length;
        bestDepth = depth;
      }
    }

    for (const child of node.children ?? []) {
      visit(child, depth + 1);
    }
  }

  for (const node of nodes) {
    visit(node, 0);
  }

  return best;
}
